import {
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { PlusIcon, UploadSimpleIcon, XIcon } from '@phosphor-icons/react';
import { DateTime, Num, T, Var, useGT } from 'gt-react';
import Page from 'src/components/page/Page';
import {
  type ColumnDef,
  type ReactTable,
  paginatedAppTableFeatures,
  useTable,
} from '@/lib/tanstack-table';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { ProductsV2Autocomplete } from '@/components/products/ProductsV2Autocomplete';
import ProductPill from '@/components/products/ProductV2Pill';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  canLoadMatches,
  editMatch,
  listQuery,
  normalizeFilterValue,
  pageCount,
  pageForFilters,
  showingRange,
  type ListQuery,
  type MatchFilters,
  type MatchListResult,
  type MatchListRow,
  type MatchModalState,
  type PageState,
} from './productMatchFilters';

export interface MatchListLoaderProps {
  query: ListQuery;
  enabled: boolean;
  children: (list: MatchListResult) => ReactNode;
}

export interface ProductMatchesCopy {
  title: ReactNode;
  subtitle: ReactNode;
  emptyTitle: ReactNode;
  emptyBody: ReactNode;
  loadingTitle: ReactNode;
}

interface PageProps {
  filters: MatchFilters;
  onFiltersChange: (filters: MatchFilters) => void;
  onRefresh: () => void;
  copy: ProductMatchesCopy;
  partyColumnHeader: string;
  termsColumnHeader: string;
  MatchLoader: (props: MatchListLoaderProps) => ReactNode;
  renderPartyControl: (
    partyId: string | undefined,
    onSelect: (id: string | undefined) => void,
  ) => ReactNode;
  renderPartyPill: (partyId: string) => ReactNode;
  renderUpload: (slot: {
    defaultPartyId?: string;
    onClose: () => void;
    onUploaded: (partyId: string) => void;
  }) => ReactNode;
  renderMapping: (slot: {
    mode: 'create' | 'edit';
    match?: MatchListRow;
    defaultPartyId?: string;
    onClose: () => void;
    onSaved: () => void;
  }) => ReactNode;
  onUploadedParty: (partyId: string) => void;
  modal?: MatchModalState<MatchListRow>;
  onModalChange?: (modal: MatchModalState<MatchListRow>) => void;
}

function TermsBadges({ terms }: { terms: string[] | null }) {
  if (!terms || terms.length === 0) {
    return (
      <span className="text-muted-foreground text-sm italic">
        <T context="empty product-match terms cell">No terms</T>
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {terms.map((term) => (
        <Badge key={term} variant="secondary">
          {term}
        </Badge>
      ))}
    </div>
  );
}

function ProductCell({ row }: { row: MatchListRow }) {
  return (
    <div className="space-y-1">
      <div className="font-medium">{row.productName}</div>
      {row.variantName ? (
        <div className="text-sm text-muted-foreground">{row.variantName}</div>
      ) : null}
      {row.variantSku ? <SkuLine sku={row.variantSku} /> : null}
    </div>
  );
}

function SkuLine({ sku }: { sku: string }) {
  return (
    <div className="text-xs text-muted-foreground">
      <T context="product variant SKU label">
        SKU: <Var>{sku}</Var>
      </T>
    </div>
  );
}

function UpdatedCell({ value }: { value: Date | string }) {
  const updatedAt = value instanceof Date ? value : new Date(String(value));
  return (
    <div className="text-sm text-muted-foreground">
      <DateTime options={{ month: 'short', day: 'numeric', year: 'numeric' }}>
        {updatedAt}
      </DateTime>
    </div>
  );
}

function buildMatchColumns(
  partyHeader: string,
  termsHeader: string,
  productHeader: string,
  updatedHeader: string,
): ColumnDef<MatchListRow>[] {
  return [
    {
      accessorKey: 'partyName',
      header: partyHeader,
      cell: ({ row }) => (
        <div className="font-medium">{row.original.partyName}</div>
      ),
      size: 200,
    },
    {
      accessorKey: 'terms',
      header: termsHeader,
      cell: ({ row }) => <TermsBadges terms={row.original.terms} />,
      size: 300,
    },
    {
      accessorKey: 'productName',
      header: productHeader,
      cell: ({ row }) => <ProductCell row={row.original} />,
      size: 300,
    },
    {
      accessorKey: 'updatedAt',
      header: updatedHeader,
      cell: ({ row }) => <UpdatedCell value={row.original.updatedAt} />,
      size: 120,
    },
  ];
}

function FilterPill({
  label,
  onClear,
  children,
}: {
  label: string;
  onClear: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      {children}
      <button
        type="button"
        onClick={onClear}
        className="hover:text-destructive"
        title={label}
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function ActiveFilters({
  filters,
  removeLabel,
  onClearParty,
  onClearProduct,
  renderPartyPill,
}: {
  filters: MatchFilters;
  removeLabel: string;
  onClearParty: () => void;
  onClearProduct: () => void;
  renderPartyPill: (partyId: string) => ReactNode;
}) {
  if (!canLoadMatches(filters)) return null;
  return (
    <>
      {filters.partyId ? (
        <FilterPill label={removeLabel} onClear={onClearParty}>
          {renderPartyPill(filters.partyId)}
        </FilterPill>
      ) : null}
      {filters.productId ? (
        <FilterPill label={removeLabel} onClear={onClearProduct}>
          <ProductPill productId={filters.productId} />
        </FilterPill>
      ) : null}
    </>
  );
}

function MatchEmpty({ copy }: { copy: ProductMatchesCopy }) {
  return (
    <div className="p-3">
      <div className="text-center space-y-2">
        <p className="text-lg font-medium">{copy.emptyTitle}</p>
        <p className="text-sm text-muted-foreground">{copy.emptyBody}</p>
      </div>
    </div>
  );
}

function MatchLoading({ title }: { title: ReactNode }) {
  return (
    <div className="p-3">
      <div className="flex flex-col items-center justify-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <div className="text-center space-y-2">
          <p className="text-lg font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">
            <T context="loading may be slow hint">This may take a while</T>
          </p>
        </div>
      </div>
    </div>
  );
}

function PaginationSummary({
  pagination,
}: {
  pagination: { offset: number; limit: number; total: number };
}) {
  const range = showingRange(pagination);
  return (
    <div className="text-sm text-muted-foreground text-center">
      <T context="pagination summary for product matches">
        Showing <Num>{range.start}</Num> to <Num>{range.end}</Num> of{' '}
        <Num>{range.total}</Num> matches
      </T>
    </div>
  );
}

function MatchResults({
  canQuery,
  isReady,
  copy,
  table,
  pagination,
  onRowSelect,
}: {
  canQuery: boolean;
  isReady: boolean;
  copy: ProductMatchesCopy;
  table: ReactTable<MatchListRow>;
  pagination?: { offset: number; limit: number; total: number };
  onRowSelect: (row: MatchListRow) => void;
}) {
  if (!canQuery) return <MatchEmpty copy={copy} />;
  if (!isReady) return <MatchLoading title={copy.loadingTitle} />;
  return (
    <>
      <div>
        <DataTable table={table} onRowClick={onRowSelect} />
        <DataTablePagination table={table} />
      </div>
      {pagination && pagination.total > 0 ? (
        <PaginationSummary pagination={pagination} />
      ) : null}
    </>
  );
}

function PageActions({
  onUpload,
  onCreate,
}: {
  onUpload: () => void;
  onCreate: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" onClick={onUpload}>
        <UploadSimpleIcon className="h-4 w-4" />
        <T context="button to bulk upload product mappings from a spreadsheet">
          Upload Mappings
        </T>
      </Button>
      <Button type="button" onClick={onCreate}>
        <PlusIcon className="h-4 w-4" />
        <T context="button to add a product mapping">New Mapping</T>
      </Button>
    </div>
  );
}

function ProductMatchesTable({
  filters,
  onFiltersChange,
  onRowSelect,
  partyColumnHeader,
  termsColumnHeader,
  MatchLoader,
  renderPartyControl,
  renderPartyPill,
  copy,
}: Pick<
  PageProps,
  | 'filters'
  | 'onFiltersChange'
  | 'partyColumnHeader'
  | 'termsColumnHeader'
  | 'MatchLoader'
  | 'renderPartyControl'
  | 'renderPartyPill'
  | 'copy'
> & { onRowSelect: (row: MatchListRow) => void }) {
  const [page, setPage] = useState({ pageIndex: 0, pageSize: 25 });
  const [seenFilters, setSeenFilters] = useState(filters);
  if (
    seenFilters.partyId !== filters.partyId ||
    seenFilters.productId !== filters.productId
  ) {
    setSeenFilters(filters);
    const nextPage = pageForFilters(seenFilters, filters, page);
    if (nextPage !== page) setPage(nextPage);
  }
  const canQuery = canLoadMatches(filters);
  const query = listQuery(filters, page);

  return (
    <MatchLoader query={query} enabled={canQuery}>
      {(list) => (
        <MatchTableBody
          list={list}
          page={page}
          onPageChange={setPage}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onRowSelect={onRowSelect}
          partyColumnHeader={partyColumnHeader}
          termsColumnHeader={termsColumnHeader}
          renderPartyControl={renderPartyControl}
          renderPartyPill={renderPartyPill}
          copy={copy}
          canQuery={canQuery}
        />
      )}
    </MatchLoader>
  );
}

function MatchTableBody({
  list,
  page,
  onPageChange,
  filters,
  onFiltersChange,
  onRowSelect,
  partyColumnHeader,
  termsColumnHeader,
  renderPartyControl,
  renderPartyPill,
  copy,
  canQuery,
}: {
  list: MatchListResult;
  page: PageState;
  onPageChange: Dispatch<SetStateAction<PageState>>;
  filters: MatchFilters;
  onFiltersChange: (filters: MatchFilters) => void;
  onRowSelect: (row: MatchListRow) => void;
  partyColumnHeader: string;
  termsColumnHeader: string;
  renderPartyControl: PageProps['renderPartyControl'];
  renderPartyPill: PageProps['renderPartyPill'];
  copy: ProductMatchesCopy;
  canQuery: boolean;
}) {
  const gt = useGT();
  const columns = buildMatchColumns(
    partyColumnHeader,
    termsColumnHeader,
    gt('Maps to Product', {
      context: 'product matches table column',
    }),
    gt('Last Updated', {
      context: 'product matches table column',
    }),
  );
  const table = useTable({
    features: paginatedAppTableFeatures,
    columns,
    data: list.rows,
    manualPagination: true,
    pageCount: list.pagination
      ? pageCount(list.pagination.total, list.pagination.limit)
      : 0,
    state: { pagination: page },
    onPaginationChange: onPageChange,
  });
  const removeLabel = gt('Remove filter', {
    context: 'clear selected filter pill',
  });
  const changeFilters = (next: MatchFilters) => {
    onFiltersChange(next);
  };

  return (
    <div className="space-y-2">
      <DataTableToolbar table={table}>
        {renderPartyControl(filters.partyId, (id) =>
          changeFilters({
            ...filters,
            partyId: normalizeFilterValue(id),
          }),
        )}
        <ProductsV2Autocomplete
          selectedProductId={filters.productId}
          onProductSelect={(id) =>
            changeFilters({
              ...filters,
              productId: normalizeFilterValue(id),
            })
          }
          allowClear
          className="w-[200px]"
        />
        <ActiveFilters
          filters={filters}
          removeLabel={removeLabel}
          onClearParty={() => changeFilters({ ...filters, partyId: undefined })}
          onClearProduct={() =>
            changeFilters({ ...filters, productId: undefined })
          }
          renderPartyPill={renderPartyPill}
        />
      </DataTableToolbar>
      <MatchResults
        canQuery={canQuery}
        isReady={list.isReady}
        copy={copy}
        table={table}
        pagination={list.pagination}
        onRowSelect={onRowSelect}
      />
    </div>
  );
}

function MatchModals({
  modal,
  filters,
  onClose,
  onRefresh,
  onUploadedParty,
  renderUpload,
  renderMapping,
}: Pick<
  PageProps,
  'filters' | 'onRefresh' | 'onUploadedParty' | 'renderUpload' | 'renderMapping'
> & { modal: MatchModalState<MatchListRow>; onClose: () => void }) {
  if (modal?.mode === 'upload') {
    return renderUpload({
      defaultPartyId: filters.partyId,
      onClose,
      onUploaded: (partyId) => {
        onRefresh();
        onUploadedParty(partyId);
      },
    });
  }
  if (!modal) return null;
  return renderMapping({
    mode: modal.mode,
    match: editMatch(modal),
    defaultPartyId: filters.partyId,
    onClose,
    onSaved: onRefresh,
  });
}

export function ProductMatchesPage(props: PageProps) {
  const [internalModal, setInternalModal] =
    useState<MatchModalState<MatchListRow>>(null);
  const modal = props.onModalChange ? (props.modal ?? null) : internalModal;
  const setModal = props.onModalChange ?? setInternalModal;
  return (
    <Page
      title={props.copy.title}
      subtitle={props.copy.subtitle}
      actions={
        <PageActions
          onUpload={() => setModal({ mode: 'upload' })}
          onCreate={() => setModal({ mode: 'create' })}
        />
      }
    >
      <ProductMatchesTable
        filters={props.filters}
        onFiltersChange={props.onFiltersChange}
        onRowSelect={(match) => setModal({ mode: 'edit', match })}
        partyColumnHeader={props.partyColumnHeader}
        termsColumnHeader={props.termsColumnHeader}
        MatchLoader={props.MatchLoader}
        renderPartyControl={props.renderPartyControl}
        renderPartyPill={props.renderPartyPill}
        copy={props.copy}
      />
      <MatchModals
        modal={modal}
        filters={props.filters}
        onClose={() => setModal(null)}
        onRefresh={props.onRefresh}
        onUploadedParty={props.onUploadedParty}
        renderUpload={props.renderUpload}
        renderMapping={props.renderMapping}
      />
    </Page>
  );
}
